import { CssTransition, type CssTransitionProps } from '@mui/base'
import {
  TransitionContext,
  useTransitionTrigger,
} from '@mui/base/useTransition'
import {
  type FC,
  type PropsWithChildren,
  useDeferredValue,
  useEffect,
} from 'react'

type UnstyledCssTransitionProps = CssTransitionProps &
  PropsWithChildren<{
    in: boolean
    onEnter?: () => void
    onExited?: () => void
  }>

export const UnstyledCssTransition: FC<UnstyledCssTransitionProps> = (
  props,
) => {
  const { children, in: open, onEnter, onExited, ...rest } = props

  const { contextValue, hasExited } = useTransitionTrigger(open)

  /*
   * This is workaround for bug https://github.com/mui/material-ui/issues/40894.
   * Issue happens as `hasExited` changes too late and `onExited` is executed before animation starts.
   */
  const deferredHasExited = useDeferredValue(hasExited)

  useEffect(() => {
    if (contextValue.requestedEnter) {
      onEnter?.()
    }
  }, [contextValue.requestedEnter, onEnter])

  useEffect(() => {
    if (deferredHasExited) {
      onExited?.()
    }
  }, [deferredHasExited, onExited])

  return (
    <TransitionContext.Provider value={contextValue}>
      <CssTransition {...rest}>{children}</CssTransition>
    </TransitionContext.Provider>
  )
}
